import { useEffect, Component, type ReactNode, type ErrorInfo } from "react";
import { QueryProvider } from "@/providers/QueryProvider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/providers/AuthProvider";
import { BryntumThemeProvider } from "@/contexts/BryntumThemeContext";
import { setApiUrl } from "@/lib/api-config";
import { configureAuth } from "@/lib/auth-fetch";
import App from "@/App";

interface AppContainerProps {
  settings?: {
    apiUrl?: string;
    clientId?: string;
    defaultUrl?: string;
    identityUrl?: string;
    redirectUri?: string;
    scope?: string;
    workspaceUrl?: string;
  };
  defaultDataMode?: 'demo' | 'live';
}

// ── Error Boundary ─────────────────────────────────────────────────────────────
interface EBState { error: Error | null }
class AppErrorBoundary extends Component<{ children: ReactNode }, EBState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[AppErrorBoundary]', error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 24, fontFamily: 'monospace', background: '#fff', color: '#c00', whiteSpace: 'pre-wrap', fontSize: 13 }}>
          <strong>App crashed — see console for details</strong>{'\n\n'}
          {this.state.error.toString()}{'\n\n'}
          {this.state.error.stack}
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function AppContainer({ settings, defaultDataMode }: AppContainerProps = {}): React.ReactElement {
  const { getAccessToken, instanceId, evoOrganisationId } = useAuth();

  // Set apiUrl synchronously before render to ensure it's available for API calls
  if (settings?.apiUrl) {
    console.log('[AppContainer] Setting API URL to:', settings.apiUrl);
    setApiUrl(settings.apiUrl);
  } else {
    console.log('[AppContainer] No apiUrl in settings, not calling setApiUrl');
  }

  // Set apiUrl synchronously before render to ensure it's available for API calls
  if (settings?.apiUrl) {
    setApiUrl(settings.apiUrl);
  }

  useEffect(() => {
    configureAuth({ getAccessToken, instanceId, evoOrganisationId });
  }, [getAccessToken, instanceId, evoOrganisationId]);

  return (
    <AppErrorBoundary>
      <QueryProvider>
        <TooltipProvider>
          <BryntumThemeProvider>
            <Toaster richColors duration={5000} closeButton />
            <App defaultDataMode={defaultDataMode} />
          </BryntumThemeProvider>
        </TooltipProvider>
      </QueryProvider>
    </AppErrorBoundary>
  );
}
